import React, { useState, createContext, useEffect } from 'react';
import axios from 'axios';

import InformationStep from '../InformationStep';
import PersonalInfoStep from '../PersonalInfoStep';
import ContactStep from '../ContactStep';
import MembershipStep from '../MembershipStep';
import StudyStep from '../StudyStep';
import MemberFeeStep from '../MemberFeeStep';
import ConfirmStep from '../ConfirmStep';
import { clear } from '@testing-library/user-event/dist/clear';

// Create the context
const FormDataContext = createContext();

const FormDataProvider = ({ children }) => {

    const [formData, changeFormData] = useState({});

    var steps = [InformationStep, PersonalInfoStep, ContactStep, MembershipStep, StudyStep, MemberFeeStep, ConfirmStep];
    const [currentStep, changeCurrentStep] = useState(0);
    const [stepList, changeStepList] = useState(steps);
    const [currentStepValid, changeCurrentStepValid] = useState(false);
    const [submitLoading, changeSubmitLoading] = useState(false);
    const [formErrors, changeFormErrors] = useState([]);
    const [currentStepHasInputs, changeCurrentStepHasInputs] = useState(false);
    const [originalNextButtonText, changeOriginalNextButtonText] = useState('Nästa');
    const [draftID, changeDraftID] = useState(null);
    const [uploadedFiles, changeUploadedFiles] = useState([]);
    
    // Output formData to console
    useEffect(() => {

        validateCurrentStepFormInputs(false);

    }, [formData]);

    useEffect(() => {
        
        if (!currentStepValid || currentStepHasInputs == false)
            return () => false;


        // Get the next button and set it to loading
        var nextButton = document.querySelector('.form__buttons.next');
        if (nextButton) {
            nextButton.classList.add('disabled');
        }

        const delayDebounceFn = setTimeout(() => {

            if (nextButton) {
                nextButton.innerHTML = 'Laddar...';
            }

            // Append all formData with -svt
            var formDataWithSvt = {};
            Object.keys(formData).forEach(key => {
                formDataWithSvt['svt-'+key] = formData[key];
            });

            if (draftID) {
                // add form_petition_draft_pid to formDataWithSvt
                formDataWithSvt['form_petition_draft_pid'] = draftID;
            }

            // Make post request to check that the data is valid
            axios.post('https://svenskatecknare.dev.qte.nu/wp-admin/admin-ajax.php', {
                action: 'verify_fields',
                ...formDataWithSvt
            }, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            })
            .then(response => {

                if (response.data.errors) {
                    changeFormErrors(response.data.errors);
                }
                else {
                    changeFormErrors([]);
                }
                
                if (response.data.draft_pid){
                    changeDraftID(response.data.draft_pid);
                }

                // Get the next button and set it to loading
                if (nextButton) {

                    // If we are not on the last step, set the button text to "Nästa"
                    if (currentStep < stepList.length - 1)
                        nextButton.innerHTML = originalNextButtonText;
                    else
                        nextButton.innerHTML = 'Skicka';

                    if (!response.data.errors)
                    nextButton.classList.remove('disabled');
                }

            });

            // Upload files. GEt all files from formData, all the keys ending with _files
            var files = Object.keys(formData).filter(key => key.endsWith('_files'));

            files.forEach(fileKey => {
                var files = formData[fileKey];

                files.forEach(file => {

                    // Do not upload the same file twice. We use lastModified as a unique identifier.
                    if (uploadedFiles.includes(file.lastModified)) {
                        return;
                    }

                    var formFileData = new FormData();
                    formFileData.append('action', 'handle_plupload');
                    formFileData.append('petition-file', file);
                    formFileData.append('form_petition_draft_pid', draftID);
                    formFileData.append('name', file.name);

                    axios.post('https://svenskatecknare.dev.qte.nu/wp-admin/admin-ajax.php', formFileData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    })
                    .then(response => {
                        if (!response.data.errors) {

                            // Update the formData with the material_pid
                            var material_pid = response.data.material_pid;
                            formData[fileKey].forEach(formDataFile => {
                                if (formDataFile.name == file.name) {
                                    formDataFile.material_pid = material_pid;
                                }
                            });

                            changeFormData(formData);

                            changeUploadedFiles([...uploadedFiles, file.lastModified]);
                        }
                        else {
                            changeFormErrors([...formErrors, response.data.errors]);
                        }
                    });
                });
            }
        );

        }, 200)
        
        return () => clearTimeout(delayDebounceFn)

    }, [formData, currentStepValid, currentStepHasInputs]);

    const submitForm = () => {

        // Append all formData with -svt
        var formDataWithSvt = {};
        Object.keys(formData).forEach(key => {
            formDataWithSvt['svt-'+key] = formData[key];
        });

        if (draftID) {
            // add form_petition_draft_pid to formDataWithSvt
            formDataWithSvt['form_petition_draft_pid'] = draftID;
        }

        // get the button and set it to loading
        var submitButton = document.querySelector('.form__buttons.next');
        if (submitButton) {
            submitButton.innerHTML = 'Skickar...';
            submitButton.classList.add('disabled');
        }

        axios.post('https://svenskatecknare.dev.qte.nu/wp-admin/admin-ajax.php', {
                action: 'submit_petition',
                ...formDataWithSvt
            }, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            })
            .then(response => {

                if (!response.data.success) {
                    changeFormErrors(response.data.errors);
                    if (submitButton) {
                        submitButton.innerHTML = 'Något fel uppstod. Försök igen.';
                        submitButton.classList.remove('disabled');
                    }
                }

                else {
                    // Post to parent window that the form has been submitted
                    window.parent.postMessage({ event: 'formSubmitted', data: formData }, '*');
                }

            }).catch(error => {
                console.log(error);
            });
    };

    //function to change step
    const changeStep = (add) => {
        if (add) {
            changeCurrentStep(currentStep + 1);
        } else {
            changeCurrentStep(currentStep - 1);
        }

        // Scroll to top of page
        window.scrollTo(0, 0);

        fillCurrentStepFormData();
    }

    const fillCurrentStepFormData = () => {
        // We need to wait for the step to change before we can update the form data
        setTimeout(() => {

            var inputs = document.querySelectorAll('input, textarea, select');
            inputs.forEach(input => {
                
                if (input.type == 'file') {

                    // If there are files in the formData, then create a new file list and add them to the input
                    if (formData[input.name]) {
                        let list = new DataTransfer();
                        formData[input.name].forEach(file => {
                            let currentFile = new File([file], file.name);
                            list.items.add(currentFile);
                        });

                        let myFileList = list.files;
                        input.files = myFileList;
                    }

                }

                else if (input.type !== "checkbox") {
                    // If the name of this input corresponds to a key in the formData, then set the value
                    if (formData[input.name]) {
                        input.value = formData[input.name];
                    }
                    else {
                        input.value = "";
                    }
                }

                else {
                    // It's a checkbox, but we only fill it if the value is not an array (i.e. it's a single value)
                    if (formData[input.name] && !Array.isArray(formData[input.name])) {
                        // If the value of this checkbox is in the formData with dataKey, then check it
                        if (formData[input.name] === input.value) {
                            input.checked = true;
                        }
                        else {
                            input.checked = false;
                        }
                    }
                }
            });

            var multiSelects = document.querySelectorAll('[multi-select]');
            multiSelects.forEach(multiSelect => {
                // Find all checkboxes that are children of this element, and add them as data if they are checked
                var dataKey = multiSelect.getAttribute("name");
                var checkboxes = multiSelect.querySelectorAll('input[type="checkbox"]');
                    checkboxes.forEach(checkbox => {
                        // If the value of this checkbox is in the formData with dataKey, then check it
                        if (formData[dataKey] && formData[dataKey].indexOf(checkbox.value) !== -1) {
                            checkbox.checked = true;
                        }
                        else {
                            checkbox.checked = false;
                        }
                    }
                );
            });


        }, 1);

        validateCurrentStepFormInputs(false);
    };

    async function validateCurrentStepFormInputs (showError=true) {

        changeCurrentStepHasInputs(false);

        setTimeout(() => {

        // Do form validation. Loop through all inputs and check if they are valid
        var inputs = document.querySelectorAll('input, textarea, select');
        var isValid = true;
        inputs.forEach(input => {
            // If the input is required, and it is empty, it is not valid
            if (input.required && input.value === '') {
                isValid = false;

                if (showError)
                    input.parentElement.classList.add('invalid');
            }
            // If the input is a number, and it is not a number, it is not valid
            if (input.type === 'number' && isNaN(input.value)) {
                isValid = false;

                if (showError)
                    input.parentElement.classList.add('invalid');
            }
            // If the input is a checkbox, and it is not checked, it is not valid
            if (input.required && input.type === 'checkbox' && !input.checked) {
                isValid = false;
                
                if (showError)
                    input.parentElement.classList.add('invalid');
            }

            if (input.required)
                changeCurrentStepHasInputs(true);

            
        });

        // Check the multi select inputs
        var multiSelects = document.querySelectorAll('[multi-select]');
        multiSelects.forEach(multiSelect => {
            // If the input is required, and it is empty, it is not valid
            if (multiSelect.hasAttribute('required') && multiSelect.querySelectorAll('input[type="checkbox"]:checked').length === 0) {
                isValid = false;

                if (showError)
                    multiSelect.classList.add('invalid');
            }

            if (multiSelect.hasAttribute('required'))
                changeCurrentStepHasInputs(true);
        });

        // Check the single select inputs
        var singleSelects = document.querySelectorAll('[single-select]');
        singleSelects.forEach(singleSelect => {
            // If the input is required, and it is empty, it is not valid
            if (singleSelect.hasAttribute('required') && singleSelect.querySelectorAll('input[type="radio"]:checked').length === 0) {
                isValid = false;

                if (showError)
                    singleSelect.classList.add('invalid');
            }

            if (singleSelect.hasAttribute('required'))
                changeCurrentStepHasInputs(true);
        });

        // If there are divs with the class "step-bubble", then the current step has inputs.
        var stepBubbles = document.querySelectorAll('.step-bubble');
        if (stepBubbles.length > 0) {
            changeCurrentStepHasInputs(true);
        }

        changeCurrentStepValid(isValid);

        }, 1);
    }

    const handleStepChoise = (e, nextComponent=null, extraData={}) => {


        // First, we need to remove any values already set in formData for this step.
        // This is because the user may have filled in some values, then gone back to a previous step and changed something.
        var parent = e.currentTarget.parentElement;
        var inputs = parent.querySelectorAll('.step-bubble');
        inputs.forEach(input => {
            var key = input.getAttribute('name');
            changeFormData({ ...formData, [key]: null });
        });

        var key = e.currentTarget.getAttribute('name');
        var value = e.currentTarget.getAttribute('value');
        changeFormData({ ...formData, [key]: value });

        if (extraData) {
            Object.keys(extraData).forEach(key => {
                changeFormData({ ...formData, [key]: extraData[key] });
            });
        }

        setTimeout(() => {
            if (!nextComponent)
            changeStep(true);

            else 
                changeNextStepComponent(nextComponent);
        }, 10);
    }

    const changeNextStepComponent = (nextComponent) => {
        steps[currentStep + 1] = nextComponent;

        const uniqueSteps = [];
        steps.map(step => {
            if (uniqueSteps.indexOf(step) === -1) {
                uniqueSteps.push(step)
            }
        });
        changeStepList(uniqueSteps);
        changeStep(true);
    }
    
    const updateFormData = (e) => {
        if (e.target.type === 'text' || e.target.type === 'email' || e.target.type === 'select-one' || e.target.type === 'number' || e.target.type === 'date' || e.target.type === 'textarea' || e.target.type === 'checkbox') {
            var dataKey = e.target.name;
            var dataValue = e.target.value;

            // If the input is a checkbox, then set value to true/false denpending on if it is checked
            if (e.target.type === 'checkbox') {
                dataValue = e.target.checked;
            }

            changeFormData({...formData, [dataKey]: dataValue});
        }
    }

    const handleFileChange = (e) => {
      const files = Array.prototype.slice.call(e.target.files);
      const uploadedFile = files[0];
      //check if uploaded file is undefined
      if (uploadedFile === undefined) {
        return;
      }

      // Check if the file is in formData, if not, add it. Make sure it's an array so we can add multiple files
        if (!formData[e.target.name]) {
            var currentUploadedFiles = [];
            currentUploadedFiles.push(uploadedFile);
            changeFormData({...formData, [e.target.name]: currentUploadedFiles});
        }
        else {
            var currentUploadedFiles = formData[e.target.name];
            currentUploadedFiles.push(uploadedFile);
            changeFormData({...formData, [e.target.name]: currentUploadedFiles});
        }
    };

    const handleSingleFormDataChange = (keyValues = {}) => {
        const newFormData = {...formData, ...keyValues};
        changeFormData(newFormData);
    }

    const handleFileRemove = (e) => {

        var material_pid = e.currentTarget.getAttribute("pid");
        var formFileData = new FormData();
        formFileData.append('action', 'delete_merit');
        formFileData.append('svt_merits_pid', material_pid);
        formFileData.append('form_petition_draft_pid', draftID);

        axios.post('https://svenskatecknare.dev.qte.nu/wp-admin/admin-ajax.php', formFileData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
        .then(response => {
            
        });

        const newFiles = [...formData[e.currentTarget.getAttribute("name")]];
        const index = newFiles.indexOf(e.currentTarget.value);
        newFiles.splice(index, 1);
        if (newFiles.length === 0) {
            // If there are no files left, remove the key from formData
            delete formData[e.currentTarget.getAttribute("name")];
            // Force a re-render
            changeFormData({...formData});
        }
        else
            changeFormData({...formData, [e.currentTarget.getAttribute("name")]: newFiles});


        // Set the value of the input to null, so that the user can upload the same file again. Find the input by name and set value to null
        var fileInput = document.querySelector('input[name="' + e.currentTarget.getAttribute("name") + '"]');
        fileInput.value = null;
    }

    const updateMultiSelectData = (e) => {
        // Find chechboxes that are children of this element, and add them as data if they are checked
        var dataKey = e.currentTarget.getAttribute("name");
        var dataValue = [];
        var checkboxes = e.currentTarget.querySelectorAll('input[type="checkbox"]');
            checkboxes.forEach(checkbox => {
                if (checkbox.checked) {
                    dataValue.push(checkbox.value);
                }
            }
        );
        changeFormData({...formData, [dataKey]: dataValue});
    }

    const updateSingleSelectData = (e) => {
        var dataKey = e.currentTarget.getAttribute("name");
        var dataValue = '';
        var radioButtons = e.currentTarget.querySelectorAll('input[type="radio"]');
            radioButtons.forEach(radioButton => {
                if (radioButton.checked) {
                    dataValue = radioButton.value;
                }
            }
        );
        changeFormData({...formData, [dataKey]: dataValue});
    }

    const closeForm = () => {
        // Send post message to parent window
        window.parent.postMessage({ event: 'closeForm' }, '*');
    }

    return (
        <FormDataContext.Provider value={{
            stepList,
            currentStep,
            formData,
            currentStepValid,
            submitLoading,
            formErrors,
            changeStepList,
            changeCurrentStep,
            changeStep,
            changeNextStepComponent,
            updateFormData,
            updateSingleSelectData,
            updateMultiSelectData,
            validateCurrentStepFormInputs,
            handleFileChange,
            handleFileRemove,
            handleStepChoise,
            closeForm,
            submitForm,
            handleSingleFormDataChange
        }}>
            {children}
        </FormDataContext.Provider>
    )
  };

export { FormDataContext, FormDataProvider };